import { Flame } from 'lucide-react';

interface ProgressPanelProps {
  coursePct: number
  attendancePct: number
  streakDays: number
}

/** 72px ring: 5px stroke on a #f3f4f6 track, matching the design. */
function Ring({ value, color }: { value: number; color: string }) {
  const size = 72;
  const strokeWidth = 5;
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, value));

  return (
    <div className="relative h-[72px] w-[72px] shrink-0">
      <svg width={size} height={size} aria-hidden="true" className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F3F4F6" strokeWidth={strokeWidth} />
        {/* Skipped at 0 — a round linecap on a zero-length dash still paints a dot. */}
        {clamped > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${(clamped / 100) * circ} ${circ}`}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[14px] font-bold leading-[21px] text-[#111827]">{value}%</span>
      </div>
    </div>
  );
}

function Stat({ visual, value, label }: { visual: React.ReactNode; value: string; label: string }) {
  return (
    <div className="flex flex-1 items-center gap-4">
      {visual}
      <div className="min-w-0">
        <p className="text-[20px] font-bold leading-[30px] tracking-[-0.5px] text-[#111827]">{value}</p>
        <p className="text-[12px] leading-[18px] text-[#9ca3af]">{label}</p>
      </div>
    </div>
  );
}

export function ProgressPanel({ coursePct, attendancePct, streakDays }: ProgressPanelProps) {
  return (
    <section aria-label="Progress snapshot">
      <h3 className="text-[16px] font-semibold leading-[24px] text-[#111827]">Progress Snapshot</h3>

      <div className="mt-4 flex min-h-[120px] flex-col items-stretch gap-6 rounded-[20px] border border-[#ececec] bg-white px-6 py-[25px] shadow-[0_1px_2px_rgba(0,0,0,0.04)] sm:flex-row sm:items-center sm:gap-0 lg:px-[33px]">
        <Stat
          visual={<Ring value={coursePct} color="#FF5A1F" />}
          value={`${coursePct}%`}
          label="Overall Progress"
        />

        <span className="hidden h-[56px] w-px shrink-0 bg-[#f3f4f6] sm:block" />

        <Stat
          visual={<Ring value={attendancePct} color="#22C55E" />}
          value={`${attendancePct}%`}
          label="Attendance"
        />

        <span className="hidden h-[56px] w-px shrink-0 bg-[#f3f4f6] sm:block" />

        <Stat
          visual={
            <span className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full bg-[#f5f3ff]">
              <Flame className="h-7 w-7 text-[#8b5cf6]" />
            </span>
          }
          value={String(streakDays)}
          label="Day Streak"
        />
      </div>
    </section>
  );
}
